// @file Padlet half ellipse shape for Tldraw
// To render the half ellipse in Tldraw. This is for Jamboard half ellipse shapes when converting to Tldraw.
import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import type {
  Geometry2d,
  RecordProps,
  TLBaseShape,
  TLDefaultSizeStyle,
  TLDrawShapeSegment,
  TLOnResizeHandler,
  TLShapeId,
  TLShapePartial,
  TLUnknownShape,
} from 'tldraw'
import {
  defaultColorNames,
  DefaultColorThemePalette,
  HTMLContainer,
  Rectangle2d,
  resizeBox,
  ShapeUtil,
  T,
  toDomPrecision,
} from 'tldraw'
import 'tldraw/tldraw.css'

type PadletJamboardDrawGroupStroke = TLDrawShapeSegment & {
  points: Array<{ x: number; y: number; z?: number; velocity?: number }>
} & {
  strokeType: 'draw' | 'highlight' | 'eraser'
  strokeSize: number
  strokeColor?: typeof defaultColorNames[number]
  strokeOpacity?: number
}

export const DrawShapeSegment = T.object<PadletJamboardDrawGroupStroke>({
  type: T.literalEnum('free', 'straight'),
  points: T.arrayOf(T.object({ x: T.number, y: T.number, z: T.optional(T.number), velocity: T.optional(T.number) })),
  strokeType: T.literalEnum('draw', 'highlight', 'eraser'),
  strokeSize: T.number,
  strokeColor: T.optional(T.literalEnum(...defaultColorNames)),
  strokeOpacity: T.optional(T.number),
})

type IPadletJamboardDrawGroupShape = TLBaseShape<
  'padlet-jamboard-draw-group',
  {
    w: number
    h: number
    strokes: PadletJamboardDrawGroupStroke[]
    size: TLDefaultSizeStyle
    scale: number
    hasDoneFirstLoad?: boolean
    image?: string
  }
>

export default class PadletJamboardDrawGroupShapeUtil extends ShapeUtil<IPadletJamboardDrawGroupShape> {
  static override type = 'padlet-jamboard-draw-group' as const
  static override props: RecordProps<IPadletJamboardDrawGroupShape> = {
    w: T.number,
    h: T.number,
    strokes: T.arrayOf(DrawShapeSegment),
    size: T.literalEnum('s', 'm', 'l', 'xl'),
    scale: T.number,
    hasDoneFirstLoad: T.optional(T.boolean),
    image: T.optional(T.string),
  }

  getDefaultProps(): IPadletJamboardDrawGroupShape['props'] {
    return {
      w: 3840,
      h: 2160,
      strokes: [],
      size: 'm',
      scale: 0.5,
      hasDoneFirstLoad: true,
      image: undefined,
    }
  }

  override canEdit = (): boolean => true
  override canResize = (): boolean => true
  override isAspectRatioLocked = (): boolean => false
  ctx: Record<TLShapeId, CanvasRenderingContext2D | null> = {}

  getGeometry(shape: IPadletJamboardDrawGroupShape): Geometry2d {
    return new Rectangle2d({
      x: 0,
      y: 0,
      width: shape.props.w * shape.props.scale,
      height: shape.props.h * shape.props.scale,
      isFilled: true,
    })
  }

  override onResize: TLOnResizeHandler<any> = (
    shape,
    info,
  ): Omit<TLShapePartial<IPadletJamboardDrawGroupShape>, 'id' | 'type'> | undefined => {
    // Resize the canvas and all content
    const newShapeDimensions = resizeBox(shape, info)
    this.ctx[shape.id]?.scale(newShapeDimensions.props.w / shape.props.w, newShapeDimensions.props.h / shape.props.h)
    return newShapeDimensions
  }

  draw(shape: IPadletJamboardDrawGroupShape): CanvasRenderingContext2D {
    if (this.ctx[shape.id] != null) {
      return this.ctx[shape.id] as CanvasRenderingContext2D
    }
    const canvas = document.createElement('canvas')
    canvas.width = shape.props.w * shape.props.scale
    canvas.height = shape.props.h * shape.props.scale
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

    ctx.clearRect(0, 0, shape.props.w, shape.props.h)
    const scale = shape.props.scale
    ctx.setTransform(scale, 0, 0, scale, 0, 0)

    for (const stroke of shape.props.strokes) {
      if (stroke.strokeType === 'eraser') {
        // If stroke is eraser, remove everything in the path
        ctx.globalCompositeOperation = 'destination-out'
        ctx.strokeStyle = 'rgb(0,0,0)'
        ctx.lineWidth = getEraserStrokeSize(stroke.strokeSize, stroke.points[0].velocity ?? 0)
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        ctx.moveTo(stroke.points[0].x ?? 0, stroke.points[0].y ?? 0)
        ctx.lineTo(stroke.points[0].x ?? 0, stroke.points[0].y ?? 0)
        ctx.stroke()
        for (let i = 0; i < stroke.points.length - 1; i++) {
          const start = stroke.points[i]
          const end = stroke.points[i + 1]
          const startStrokeSize = getEraserStrokeSize(stroke.strokeSize, start.velocity)
          let endStrokeSize = getEraserStrokeSize(stroke.strokeSize, end.velocity)
          if (start.velocity != null && end.velocity != null && end.velocity < start.velocity) {
            endStrokeSize = startStrokeSize
          }
          const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y)
          gradient.addColorStop(0, 'black')
          gradient.addColorStop(1, 'black')
          ctx.strokeStyle = gradient

          const distance = Math.hypot(end.x - start.x, end.y - start.y)
          const stepCount = Math.ceil(distance / 2)
          const stepX = (end.x - start.x) / stepCount
          const stepY = (end.y - start.y) / stepCount
          const stepStrokeSize = (endStrokeSize - startStrokeSize) / stepCount

          for (let j = 0; j < stepCount; j++) {
            const x = start.x + stepX * j
            const y = start.y + stepY * j
            const strokeSize = startStrokeSize + stepStrokeSize * j

            ctx.lineWidth = strokeSize
            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(x + stepX, y + stepY)
            ctx.stroke()
          }
        }
        continue
      }
      ctx.beginPath()
      ctx.moveTo(stroke.points[0].x ?? 0, stroke.points[0].y ?? 0)
      ctx.lineTo(stroke.points[0].x ?? 0, stroke.points[0].y ?? 0)

      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x ?? 0, stroke.points[i].y ?? 0)
      }
      ctx.globalCompositeOperation = 'source-over'
      // Set the stroke color and size and style of the stroke based on strokeType and strokeColor and strokeOpacity
      ctx.strokeStyle =
        stroke.strokeColor != null
          ? stroke.strokeType === 'highlight'
            ? DefaultColorThemePalette.lightMode[stroke.strokeColor]?.highlight.srgb
            : DefaultColorThemePalette.lightMode[stroke.strokeColor]?.solid
          : 'rgb(0,0,0)'
      // Add opacity to the stroke color
      if (stroke.strokeOpacity != null && stroke.strokeOpacity < 1) {
        // Parse strokeStyle
        const strokeStyle = ctx.strokeStyle
        if (strokeStyle.startsWith('#')) {
          // Convert hex to rgb
          const hex = strokeStyle.slice(1)
          const rgb = [
            parseInt(hex.substring(0, 2), 16),
            parseInt(hex.substring(2, 4), 16),
            parseInt(hex.substring(4, 6), 16),
          ]
          ctx.strokeStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${stroke.strokeOpacity})`
        } else if (strokeStyle.startsWith('rgb')) {
          // Convert rgb to rgba
          const strokeColors = strokeStyle.slice(strokeStyle.indexOf('(') + 1, strokeStyle.indexOf(')')).split(', ')
          // Add opacity to the strokeStyle
          ctx.strokeStyle = `rgba(${strokeColors[0]}, ${strokeColors[1]}, ${strokeColors[2]}, ${stroke.strokeOpacity})`
        } else if (strokeStyle.startsWith('rgba')) {
          // Add opacity to the strokeStyle
          ctx.strokeStyle = strokeStyle.slice(0, strokeStyle.lastIndexOf(',')) + `, ${stroke.strokeOpacity})`
        }
      }
      // For eraser, we double the stroke size cuz the results looks closer to the imported Jamboard
      // ctx.lineWidth = stroke.strokeType === 'eraser' ? stroke.strokeSize * 2 : stroke.strokeSize
      ctx.lineWidth = stroke.strokeSize
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()
    }
    this.ctx[shape.id] = ctx

    return ctx
  }

  component(shape: IPadletJamboardDrawGroupShape): JSX.Element | null {
    const [isDrawn, setIsDrawn] = useState(false)
    // Draw the strokes on a canvas
    useEffect(() => {
      if (shape.props.image != null) {
        setIsDrawn(true)
        return
      }
      const ctx = this.draw(shape)
      const canvas = document.getElementById(
        'tl-padlet-jamboard-draw-group-canvas-' + shape.id.split(':')[1],
      ) as HTMLCanvasElement
      canvas
        .getContext('2d')
        ?.drawImage(ctx.canvas, 0, 0, shape.props.w * shape.props.scale, shape.props.h * shape.props.scale)
      setIsDrawn(true)
    }, [shape.props.w, shape.props.h, shape.props.strokes, shape.props.scale])
    useEffect(() => {
      // Check if the shape has any pixels and destroy it if it doesn't
      if (isDrawn && (shape.props.hasDoneFirstLoad === null || shape.props.hasDoneFirstLoad === false)) {
        this.checkAndDestroyOrSaveRenderingImage(shape)
      }
    }, [isDrawn])
    return (
      <HTMLContainer id={shape.id}>
        {shape.props.image != null && <img src={shape.props.image} />}
        {shape.props.image == null && (
          <canvas
            id={'tl-padlet-jamboard-draw-group-canvas-' + shape.id.split(':')[1]}
            width={shape.props.w * shape.props.scale}
            height={shape.props.h * shape.props.scale}
          />
        )}
      </HTMLContainer>
    )
  }

  toSvg(shape: IPadletJamboardDrawGroupShape): null | Promise<null | ReactElement> | ReactElement {
    // Return image of the canvas
    if (shape.props.image != null) {
      return <image width={shape.props.w} height={shape.props.h} href={shape.props.image} />
    }
    if (this.ctx[shape.id] == null) {
      this.draw(shape)
    }
    return (
      <image width={shape.props.w} height={shape.props.h} href={this.ctx[shape.id]?.canvas.toDataURL('image/png')} />
    )
  }

  // Check if the shape has any pixels and destroy it if it doesn't
  // Otherwise, save the image to the shape to prevent computation on every render
  checkAndDestroyOrSaveRenderingImage(shape: IPadletJamboardDrawGroupShape): void {
    // Check if there are any pixels in the canvas
    if (this.ctx[shape.id] == null) {
      return
    }
    const imageData = this.ctx[shape.id]?.getImageData(0, 0, shape.props.w, shape.props.h).data
    if (imageData == null) {
      return
    }
    let hasPixels = false

    for (let i = 0; i < imageData.length; i += 4) {
      // 4 bytes per pixel, RGBA
      if (imageData[i] > 0 || imageData[i + 1] > 0 || imageData[i + 2] > 0 || imageData[i + 3] > 0) {
        hasPixels = true
        break
      }
    }
    if (!hasPixels) {
      // Destroy the shape if there are no pixels
      this.destroy(shape.id)
    } else {
      // Save the image to the shape and set strokes to empty
      this.update({
        ...shape,
        props: {
          ...shape.props,
          strokes: [],
          hasDoneFirstLoad: true,
          image: this.ctx[shape.id]?.canvas.toDataURL('image/png'),
        },
      })
    }
  }

  destroy(id: TLShapeId): void {
    // Caution: This will remove the shape from the editor
    // It's not really optimal cuz this deletion happens on the client side on first load of the whiteboard
    this.editor.deleteShape(id)
  }

  update(shape: TLUnknownShape): void {
    // Caution: This will update the spae
    // It's not really optimal cuz this deletion happens on the client side on first load of the whiteboard
    this.editor.updateShape(shape)
  }

  indicator(shape: IPadletJamboardDrawGroupShape): JSX.Element | null {
    const bounds = this.getGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }
}

const MAX_ERASER_VELOCITY = 300000
const MAX_STROKE_SIZE_MULTIPLIER = 2.5
function getEraserStrokeSize(baseStrokeSize: number, velocity?: number): number {
  if (velocity == null) {
    return baseStrokeSize
  }
  // Calculate the stroke size based on the velocity of the stroke, use a square root function
  const ratio = Math.min(Math.sqrt(velocity / MAX_ERASER_VELOCITY), 1)
  return baseStrokeSize * (1 + (MAX_STROKE_SIZE_MULTIPLIER - 1) * ratio)
}
